import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  Check,
  ChevronDown,
  Download,
  FileText,
  FileUp,
  Filter,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  MoreHorizontal,
  Pencil,
  PenLine,
  RefreshCw,
  Search,
  SortAsc,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';
import { cn } from '@/shared/utils/utils';
import { PDFFileList } from '../../api';

const UNASSIGNED_LABEL = 'Unassigned';

export type BulkAction = 'download-original' | 'download-markup' | 'delete';

export type BulkActionState =
  | { status: 'idle' }
  | { status: 'running'; action: BulkAction; total: number; completed: number; currentFilename: string | null }
  | { status: 'success'; action: BulkAction; succeeded: number; failed: number }
  | { status: 'error'; action: BulkAction; message: string };

export type DrawingsBrowserView = 'grid' | 'list';
export type DrawingsBrowserSort = 'drawing_number' | 'recent' | 'filename';
export type DrawingsBrowserStatus = 'all' | 'ready' | 'processing' | 'error';

interface DrawingsBrowserProps {
  pdfs: PDFFileList[];
  isLoading: boolean;
  isRefetching: boolean;
  onRefresh: () => void;
  onOpen: (pdf: PDFFileList) => void;
  onUploadClick: () => void;
  onUploadRevision: (pdf: PDFFileList) => void;
  onFilesDropped: (files: File[]) => void;
  onUpdateMetadata: (
    pdf: PDFFileList,
    pageId: string,
    updates: { drawing_number?: string; title?: string; discipline?: string }
  ) => Promise<void>;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  onBulkAction: (action: BulkAction, pdfs: PDFFileList[]) => void;
  bulkActionState: BulkActionState;
}

const statusTone = (status: PDFFileList['status']): string => {
  switch (status) {
    case 'ready':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'processing':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'error':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const collectDisciplineOptions = (pdfs: PDFFileList[]): string[] => {
  const seen = new Set<string>();
  for (const pdf of pdfs) {
    const items = pdf.disciplines && pdf.disciplines.length > 0 ? pdf.disciplines : [UNASSIGNED_LABEL];
    items.forEach((item) => {
      const key = (item || UNASSIGNED_LABEL).trim() || UNASSIGNED_LABEL;
      seen.add(key);
    });
  }
  return Array.from(seen).sort((a, b) => {
    if (a === UNASSIGNED_LABEL) return 1;
    if (b === UNASSIGNED_LABEL) return -1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });
};

const matchesQuery = (pdf: PDFFileList, query: string): boolean => {
  if (!query) return true;
  const haystack = [
    pdf.original_filename,
    pdf.primary_drawing_number,
    pdf.primary_title,
    ...(pdf.disciplines ?? []),
  ].map((value) => (value ?? '').toLowerCase());
  return haystack.some((value) => value.includes(query));
};

const matchesDisciplines = (pdf: PDFFileList, selected: Set<string>): boolean => {
  if (!selected.size) return true;
  const items = pdf.disciplines && pdf.disciplines.length > 0 ? pdf.disciplines : [UNASSIGNED_LABEL];
  return items.some((item) => selected.has((item || UNASSIGNED_LABEL).trim() || UNASSIGNED_LABEL));
};

const matchesStatus = (pdf: PDFFileList, status: DrawingsBrowserStatus): boolean => {
  if (status === 'all') return true;
  return pdf.status === status;
};

const compareDrawings = (a: PDFFileList, b: PDFFileList, sort: DrawingsBrowserSort): number => {
  if (sort === 'recent') {
    const aDate = new Date(a.created_at).getTime();
    const bDate = new Date(b.created_at).getTime();
    return bDate - aDate;
  }
  if (sort === 'filename') {
    return a.original_filename.localeCompare(b.original_filename, undefined, { sensitivity: 'base' });
  }
  const aNum = (a.primary_drawing_number || '').toLowerCase();
  const bNum = (b.primary_drawing_number || '').toLowerCase();
  if (aNum && bNum && aNum !== bNum) return aNum.localeCompare(bNum, undefined, { numeric: true });
  if (aNum && !bNum) return -1;
  if (!aNum && bNum) return 1;
  return a.original_filename.localeCompare(b.original_filename, undefined, { sensitivity: 'base' });
};

const formatRelativeTime = (iso: string): string => {
  const created = new Date(iso).getTime();
  if (!Number.isFinite(created)) return '';
  const diff = Date.now() - created;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const DrawingsBrowser: React.FC<DrawingsBrowserProps> = ({
  pdfs,
  isLoading,
  isRefetching,
  onRefresh,
  onOpen,
  onUploadClick,
  onUploadRevision,
  onFilesDropped,
  onUpdateMetadata,
  selectedIds,
  onSelectedIdsChange,
  onBulkAction,
  bulkActionState,
}) => {
  const [query, setQuery] = useState('');
  const [view, setView] = useState<DrawingsBrowserView>('list');
  const [sort, setSort] = useState<DrawingsBrowserSort>('drawing_number');
  const [statusFilter, setStatusFilter] = useState<DrawingsBrowserStatus>('all');
  const [selectedDisciplines, setSelectedDisciplines] = useState<Set<string>>(new Set());
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragCounter = useRef(0);

  const disciplineOptions = useMemo(() => collectDisciplineOptions(pdfs), [pdfs]);

  const trimmedQuery = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    const filteredItems = pdfs.filter((pdf) =>
      matchesQuery(pdf, trimmedQuery)
      && matchesStatus(pdf, statusFilter)
      && matchesDisciplines(pdf, selectedDisciplines)
    );
    return filteredItems.slice().sort((a, b) => compareDrawings(a, b, sort));
  }, [pdfs, trimmedQuery, statusFilter, selectedDisciplines, sort]);

  // Group the filtered drawings into discipline buckets. A PDF that spans
  // multiple disciplines (multi-sheet upload) shows up in each bucket it
  // belongs to, mirroring the sidebar's grouping rules.
  const groups = useMemo(() => {
    const buckets = new Map<string, PDFFileList[]>();
    for (const pdf of filtered) {
      const items = pdf.disciplines && pdf.disciplines.length > 0 ? pdf.disciplines : [UNASSIGNED_LABEL];
      for (const item of items) {
        const key = (item || UNASSIGNED_LABEL).trim() || UNASSIGNED_LABEL;
        const list = buckets.get(key) ?? [];
        list.push(pdf);
        buckets.set(key, list);
      }
    }
    return Array.from(buckets.entries())
      .sort(([a], [b]) => {
        if (a === UNASSIGNED_LABEL) return 1;
        if (b === UNASSIGNED_LABEL) return -1;
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      })
      .map(([discipline, groupPdfs]) => ({ discipline, pdfs: groupPdfs }));
  }, [filtered]);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroupCollapsed = (discipline: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(discipline)) next.delete(discipline);
      else next.add(discipline);
      return next;
    });
  };
  const expandAllGroups = () => setCollapsedGroups(new Set());
  const collapseAllGroups = () => setCollapsedGroups(new Set(groups.map((group) => group.discipline)));

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedPdfs = useMemo(
    () => pdfs.filter((pdf) => selectedSet.has(pdf.id)),
    [pdfs, selectedSet]
  );

  // Drop stale selections (covered drawings removed elsewhere).
  useEffect(() => {
    if (!selectedIds.length) return;
    const valid = new Set(pdfs.map((pdf) => pdf.id));
    const next = selectedIds.filter((id) => valid.has(id));
    if (next.length !== selectedIds.length) onSelectedIdsChange(next);
  }, [pdfs, selectedIds, onSelectedIdsChange]);

  const filteredIds = useMemo(
    () => Array.from(new Set(filtered.map((pdf) => pdf.id))),
    [filtered]
  );
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedSet.has(id));
  const someFilteredSelected = filteredIds.some((id) => selectedSet.has(id));

  const handleSelectAllVisible = (next: boolean) => {
    if (next) {
      const merged = new Set(selectedIds);
      filteredIds.forEach((id) => merged.add(id));
      onSelectedIdsChange(Array.from(merged));
    } else {
      const visible = new Set(filteredIds);
      onSelectedIdsChange(selectedIds.filter((id) => !visible.has(id)));
    }
  };

  const toggleGroupSelection = (groupPdfs: PDFFileList[], next: boolean) => {
    const groupIds = new Set(groupPdfs.map((pdf) => pdf.id));
    if (next) {
      const merged = new Set(selectedIds);
      groupIds.forEach((id) => merged.add(id));
      onSelectedIdsChange(Array.from(merged));
    } else {
      onSelectedIdsChange(selectedIds.filter((id) => !groupIds.has(id)));
    }
  };

  const toggleSelection = (pdfId: string, next: boolean) => {
    if (next) {
      if (selectedSet.has(pdfId)) return;
      onSelectedIdsChange([...selectedIds, pdfId]);
    } else {
      onSelectedIdsChange(selectedIds.filter((id) => id !== pdfId));
    }
  };

  const toggleDiscipline = (discipline: string) => {
    setSelectedDisciplines((prev) => {
      const next = new Set(prev);
      if (next.has(discipline)) next.delete(discipline);
      else next.add(discipline);
      return next;
    });
  };

  const clearFilters = () => {
    setQuery('');
    setStatusFilter('all');
    setSelectedDisciplines(new Set());
    setSort('drawing_number');
  };

  const isBulkRunning = bulkActionState.status === 'running';
  const triggerBulk = (action: BulkAction) => {
    if (!selectedPdfs.length || isBulkRunning) return;
    onBulkAction(action, selectedPdfs);
  };

  const triggerRowAction = (action: BulkAction, pdf: PDFFileList) => {
    if (isBulkRunning) return;
    onBulkAction(action, [pdf]);
  };

  const isPdfFile = (file: File) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer?.types ?? []).includes('Files')) return;
    event.preventDefault();
    dragCounter.current += 1;
    setIsDraggingFiles(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDraggingFiles(false);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (Array.from(event.dataTransfer?.types ?? []).includes('Files')) {
      event.preventDefault();
    }
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!Array.from(event.dataTransfer?.types ?? []).includes('Files')) return;
      event.preventDefault();
      dragCounter.current = 0;
      setIsDraggingFiles(false);
      const files = Array.from(event.dataTransfer.files || []).filter(isPdfFile);
      if (files.length) onFilesDropped(files);
    },
    [onFilesDropped]
  );

  const headerCount = filtered.length;
  const totalCount = pdfs.length;

  const runningLabel = isBulkRunning
    ? `${bulkActionState.action === 'download-markup' ? 'Flattening' : bulkActionState.action === 'download-original' ? 'Downloading' : 'Deleting'} ${bulkActionState.completed}/${bulkActionState.total}`
    : null;

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Toolbar */}
      <div className="border-b border-border bg-card/60 backdrop-blur px-4 py-3 space-y-2.5">
        <div className="flex items-center gap-2">
          <div>
            <h2 className="text-lg font-semibold leading-tight">Project Drawings</h2>
            <p className="text-xs text-muted-foreground">
              {totalCount === 0
                ? 'No drawings yet. Upload your first PDF to get started.'
                : headerCount === totalCount
                  ? `${totalCount} drawing${totalCount === 1 ? '' : 's'}`
                  : `${headerCount} of ${totalCount} drawing${totalCount === 1 ? '' : 's'} match filters`}
            </p>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isRefetching}
              title="Refresh drawings"
            >
              {isRefetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button size="sm" onClick={onUploadClick} data-tour-id="drawings-upload">
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-tour-id="drawings-search-filters">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search filename, drawing #, title, discipline"
              className="pl-8 h-9"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setQuery('')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Discipline filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                <Filter className="h-4 w-4 mr-2" />
                Discipline
                {selectedDisciplines.size > 0 && (
                  <span className="ml-2 text-[11px] rounded-full bg-primary text-primary-foreground px-1.5">
                    {selectedDisciplines.size}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-h-72 overflow-y-auto">
              <DropdownMenuLabel>Filter by discipline</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {disciplineOptions.length === 0 ? (
                <DropdownMenuItem disabled>No disciplines yet</DropdownMenuItem>
              ) : (
                disciplineOptions.map((discipline) => (
                  <DropdownMenuCheckboxItem
                    key={discipline}
                    checked={selectedDisciplines.has(discipline)}
                    onCheckedChange={() => toggleDiscipline(discipline)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {discipline}
                  </DropdownMenuCheckboxItem>
                ))
              )}
              {selectedDisciplines.size > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setSelectedDisciplines(new Set())}>
                    Clear discipline filter
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Status filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {statusFilter === 'all' ? 'All status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as DrawingsBrowserStatus)}
              >
                <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="ready">Ready</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="processing">Processing</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="error">Error</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sort */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                <SortAsc className="h-4 w-4 mr-2" />
                {sort === 'drawing_number' ? 'Drawing #' : sort === 'recent' ? 'Recent' : 'Filename'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={sort} onValueChange={(value) => setSort(value as DrawingsBrowserSort)}>
                <DropdownMenuRadioItem value="drawing_number">Drawing #</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="recent">Recently uploaded</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="filename">Filename</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* View toggle */}
          <div className="flex rounded-md border bg-background overflow-hidden">
            <button
              type="button"
              onClick={() => setView('grid')}
              title="Grid view"
              className={cn(
                'px-2.5 h-9 inline-flex items-center text-xs',
                view === 'grid' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/60'
              )}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              title="List view"
              className={cn(
                'px-2.5 h-9 inline-flex items-center text-xs border-l',
                view === 'list' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/60'
              )}
            >
              <ListIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Selection row */}
        <div className="flex items-center gap-2 text-xs" data-tour-id="drawings-bulk-actions">
          <label className="flex items-center gap-2 select-none">
            <Checkbox
              checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false}
              onCheckedChange={(value) => handleSelectAllVisible(value === true)}
              aria-label="Select all visible drawings"
            />
            <span className="text-muted-foreground">
              {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all visible'}
            </span>
          </label>
          {selectedIds.length > 0 && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onSelectedIdsChange([])}>
              Clear selection
            </Button>
          )}
          <div className="flex-1" />
          {(query || statusFilter !== 'all' || selectedDisciplines.size > 0) && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={clearFilters}>
              Reset filters
            </Button>
          )}
          <TooltipProvider delayDuration={250}>
            {/* Download split button: primary action = originals; chevron picks variant. */}
            <div className="inline-flex rounded-md shadow-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant={selectedIds.length ? 'default' : 'outline'}
                    disabled={!selectedIds.length || isBulkRunning}
                    onClick={() => triggerBulk('download-original')}
                    className="h-7 rounded-r-none border-r-0 px-2.5 text-xs"
                  >
                    {isBulkRunning && bulkActionState.action !== 'delete' ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> {runningLabel}
                      </>
                    ) : (
                      <>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Download originals (no markup)
                </TooltipContent>
              </Tooltip>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant={selectedIds.length ? 'default' : 'outline'}
                    disabled={!selectedIds.length || isBulkRunning}
                    aria-label="Choose download variant"
                    className="h-7 rounded-l-none px-1.5"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{selectedIds.length} drawing(s)</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => triggerBulk('download-original')}>
                    <Download className="h-4 w-4 mr-2" /> Download originals
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => triggerBulk('download-markup')}>
                    <PenLine className="h-4 w-4 mr-2" /> Download with markup
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={!selectedIds.length || isBulkRunning}
              onClick={() => triggerBulk('delete')}
              className="h-7 px-2.5 text-xs text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive disabled:text-muted-foreground disabled:border-border"
            >
              {isBulkRunning && bulkActionState.action === 'delete' ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> {runningLabel}
                </>
              ) : (
                <>
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </>
              )}
            </Button>
          </TooltipProvider>
        </div>
      </div>

      {/* Main scroll area */}
      <div className="flex-1 overflow-y-auto px-4 py-4" data-tour-id="drawings-list">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading drawings...
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            hasAnyDrawings={pdfs.length > 0}
            onUploadClick={onUploadClick}
            onClearFilters={clearFilters}
          />
        ) : (
          <div className="space-y-3">
            {groups.length > 1 && (
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <button
                  type="button"
                  className="hover:text-foreground underline-offset-2 hover:underline"
                  onClick={expandAllGroups}
                >
                  Expand all
                </button>
                <span>·</span>
                <button
                  type="button"
                  className="hover:text-foreground underline-offset-2 hover:underline"
                  onClick={collapseAllGroups}
                >
                  Collapse all
                </button>
              </div>
            )}
            {groups.map((group) => {
              const isCollapsed = collapsedGroups.has(group.discipline);
              const groupIds = group.pdfs.map((pdf) => pdf.id);
              const groupSelectedCount = groupIds.filter((id) => selectedSet.has(id)).length;
              const groupAllSelected = groupIds.length > 0 && groupSelectedCount === groupIds.length;
              const groupSomeSelected = groupSelectedCount > 0 && !groupAllSelected;
              return (
                <section
                  key={group.discipline}
                  className="border rounded-lg overflow-hidden bg-card"
                >
                  <header className="flex items-center gap-2 bg-muted/40 px-3 py-2">
                    <span data-no-row-click>
                      <Checkbox
                        checked={groupAllSelected ? true : groupSomeSelected ? 'indeterminate' : false}
                        onCheckedChange={(value) => toggleGroupSelection(group.pdfs, value === true)}
                        aria-label={`Select all in ${group.discipline}`}
                      />
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleGroupCollapsed(group.discipline)}
                      className="flex flex-1 items-center gap-2 min-w-0 text-left"
                      aria-expanded={!isCollapsed}
                    >
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 text-muted-foreground transition-transform',
                          isCollapsed && '-rotate-90'
                        )}
                      />
                      <span className="text-sm font-semibold uppercase tracking-wide">
                        {group.discipline}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {groupSelectedCount > 0
                          ? `${groupSelectedCount} / ${group.pdfs.length} selected`
                          : `${group.pdfs.length} ${group.pdfs.length === 1 ? 'drawing' : 'drawings'}`}
                      </span>
                    </button>
                  </header>
                  {!isCollapsed && (
                    <div className="p-3">
                      {view === 'grid' ? (
                        <DrawingGrid
                          pdfs={group.pdfs}
                          selectedSet={selectedSet}
                          onToggleSelection={toggleSelection}
                          onOpen={onOpen}
                          onRowAction={triggerRowAction}
                          onUploadRevision={onUploadRevision}
                          isRowActionRunning={isBulkRunning}
                        />
                      ) : (
                        <DrawingTable
                          pdfs={group.pdfs}
                          selectedSet={selectedSet}
                          onToggleSelection={toggleSelection}
                          onOpen={onOpen}
                          onRowAction={triggerRowAction}
                          onUploadRevision={onUploadRevision}
                          isRowActionRunning={isBulkRunning}
                          onUpdateMetadata={onUpdateMetadata}
                        />
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      {isDraggingFiles && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary backdrop-blur-sm pointer-events-none">
          <div className="rounded-lg bg-background border shadow-lg px-6 py-5 text-center">
            <Upload className="h-8 w-8 mx-auto mb-2 text-primary" />
            <p className="text-sm font-medium">Drop PDFs to upload</p>
            <p className="text-xs text-muted-foreground">One file opens review; multiple files upload as a batch.</p>
          </div>
        </div>
      )}
    </div>
  );
};

interface EmptyStateProps {
  hasAnyDrawings: boolean;
  onUploadClick: () => void;
  onClearFilters: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ hasAnyDrawings, onUploadClick, onClearFilters }) => (
  <div className="border-2 border-dashed border-border rounded-lg py-12 px-6 flex flex-col items-center text-center text-muted-foreground">
    <div className="p-4 bg-primary/10 rounded-full mb-3">
      <FileText className="h-8 w-8 text-primary" />
    </div>
    {hasAnyDrawings ? (
      <>
        <p className="text-sm font-medium text-foreground">No drawings match your filters.</p>
        <p className="text-xs mt-1">Try clearing search or filter selections.</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onClearFilters}>
          Reset filters
        </Button>
      </>
    ) : (
      <>
        <p className="text-sm font-medium text-foreground">No drawings uploaded yet.</p>
        <p className="text-xs mt-1">Drag PDFs anywhere on this page, or click upload to get started.</p>
        <Button size="sm" className="mt-4" onClick={onUploadClick}>
          <Upload className="h-4 w-4 mr-2" />
          Upload drawings
        </Button>
      </>
    )}
  </div>
);

interface GridProps {
  pdfs: PDFFileList[];
  selectedSet: Set<string>;
  onToggleSelection: (pdfId: string, next: boolean) => void;
  onOpen: (pdf: PDFFileList) => void;
  onRowAction: (action: BulkAction, pdf: PDFFileList) => void;
  onUploadRevision: (pdf: PDFFileList) => void;
  isRowActionRunning: boolean;
}

interface DrawingTableProps extends GridProps {
  onUpdateMetadata: DrawingsBrowserProps['onUpdateMetadata'];
}

interface RowActionsProps {
  pdf: PDFFileList;
  onRowAction: (action: BulkAction, pdf: PDFFileList) => void;
  onUploadRevision: (pdf: PDFFileList) => void;
  isRowActionRunning: boolean;
  compact?: boolean;
}

const RowActions: React.FC<RowActionsProps> = ({
  pdf,
  onRowAction,
  // Upload Revision is wired but currently disabled in the UI; keep the prop
  // in the interface so the workspace can wire it back in without churn.
  onUploadRevision: _onUploadRevision,
  isRowActionRunning,
  compact = false,
}) => {
  const buttonSize = compact ? 'h-7 w-7' : 'h-8 w-8';
  return (
    <div className="flex items-center gap-0.5" data-no-row-click>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        title="Download original"
        aria-label={`Download ${pdf.original_filename}`}
        disabled={isRowActionRunning}
        onClick={() => onRowAction('download-original', pdf)}
        className={cn(buttonSize, 'rounded-md')}
      >
        <Download className="h-4 w-4" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title="More actions"
            aria-label={`More actions for ${pdf.original_filename}`}
            disabled={isRowActionRunning}
            className={cn(buttonSize, 'rounded-md')}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            disabled
            onSelect={(event) => event.preventDefault()}
            title="Coming soon"
            className="text-muted-foreground"
          >
            <FileUp className="h-4 w-4 mr-2" /> Upload Revision
            <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground/70">
              Soon
            </span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => onRowAction('delete', pdf)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const DrawingGrid: React.FC<GridProps> = ({
  pdfs,
  selectedSet,
  onToggleSelection,
  onOpen,
  onRowAction,
  onUploadRevision,
  isRowActionRunning,
}) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
    {pdfs.map((pdf) => {
      const isChecked = selectedSet.has(pdf.id);
      const disciplines = pdf.disciplines && pdf.disciplines.length > 0 ? pdf.disciplines : [UNASSIGNED_LABEL];
      return (
        <div
          key={pdf.id}
          data-tour-id="drawings-row"
          className={cn(
            'group relative rounded-lg border bg-card transition-colors flex flex-col cursor-pointer',
            isChecked ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/60'
          )}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('[data-no-row-click]')) return;
            onOpen(pdf);
          }}
        >
          <div className="aspect-[4/3] bg-muted/40 rounded-t-lg flex items-center justify-center border-b relative overflow-hidden">
            <FileText className="h-14 w-14 text-muted-foreground/40" />
            <div className="absolute top-2 left-2" data-no-row-click>
              <Checkbox
                checked={isChecked}
                onCheckedChange={(value) => onToggleSelection(pdf.id, value === true)}
                aria-label={`Select ${pdf.original_filename}`}
                className="bg-background"
              />
            </div>
            <div className="absolute bottom-2 right-2 flex flex-wrap gap-1 max-w-[calc(100%-1rem)] justify-end">
              <span className={cn('text-[10px] px-1.5 py-0.5 rounded border', statusTone(pdf.status))}>
                {pdf.status}
              </span>
            </div>
          </div>
          <div className="p-2.5 space-y-1.5">
            <div className="flex items-baseline gap-1.5 min-w-0">
              {pdf.primary_drawing_number && (
                <span className="text-xs font-mono font-semibold text-foreground shrink-0">
                  {pdf.primary_drawing_number}
                </span>
              )}
              <span className="text-xs text-foreground truncate" title={pdf.primary_title || pdf.original_filename}>
                {pdf.primary_title || pdf.original_filename}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground truncate" title={pdf.original_filename}>
              {pdf.original_filename}
            </p>
            <div className="flex flex-wrap gap-1">
              {disciplines.slice(0, 3).map((discipline) => (
                <span
                  key={`${pdf.id}-${discipline}`}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border"
                >
                  {discipline}
                </span>
              ))}
              {disciplines.length > 3 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  +{disciplines.length - 3}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{pdf.page_count} {pdf.page_count === 1 ? 'page' : 'pages'}</span>
              <span>{formatRelativeTime(pdf.created_at)}</span>
            </div>
            <div className="flex justify-end pt-1 -mx-1 -mb-1">
              <RowActions
                pdf={pdf}
                onRowAction={onRowAction}
                onUploadRevision={onUploadRevision}
                isRowActionRunning={isRowActionRunning}
                compact
              />
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

interface InlineMetadataFieldProps {
  value: string;
  emptyLabel: string;
  ariaLabel: string;
  inputClassName?: string;
  valueClassName?: string;
  onSave: (value: string) => Promise<void>;
}

const InlineMetadataField: React.FC<InlineMetadataFieldProps> = ({
  value,
  emptyLabel,
  ariaLabel,
  inputClassName,
  valueClassName,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [isEditing, value]);

  const cancel = () => {
    setDraft(value);
    setIsEditing(false);
  };

  const save = async () => {
    const nextValue = draft.trim();
    if (nextValue === value.trim()) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(nextValue);
      setIsEditing(false);
    } catch {
      // The mutation owner displays the API error. Keep the field open so the
      // user can correct or retry the value without losing their draft.
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <div className="group/edit flex max-w-full items-center gap-1.5 px-1.5 py-1">
        <span className={cn('min-w-0 truncate', value ? valueClassName : 'text-muted-foreground italic')}>
          {value || emptyLabel}
        </span>
        <button
          type="button"
          data-no-row-click
          aria-label={ariaLabel}
          className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-primary opacity-0 transition hover:bg-primary/10 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/edit:opacity-100"
          onClick={() => setIsEditing(true)}
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1" data-no-row-click>
      <Input
        autoFocus
        aria-label={ariaLabel}
        value={draft}
        disabled={isSaving}
        className={cn('h-8 px-2 text-xs', inputClassName)}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void save();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            cancel();
          }
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-primary"
        disabled={isSaving}
        aria-label={`Save ${ariaLabel.toLowerCase()}`}
        onClick={() => { void save(); }}
      >
        {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground"
        disabled={isSaving}
        aria-label={`Cancel ${ariaLabel.toLowerCase()}`}
        onClick={cancel}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

const DrawingTable: React.FC<DrawingTableProps> = ({
  pdfs,
  selectedSet,
  onToggleSelection,
  onOpen,
  onRowAction,
  onUploadRevision,
  isRowActionRunning,
  onUpdateMetadata,
}) => (
  <div className="overflow-x-auto rounded-[13px] border border-border bg-card">
    <table className="w-full min-w-[980px] text-sm">
      <thead className="bg-muted/50 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        <tr>
          <th className="w-8 px-3 py-2" />
          <th className="w-[150px] px-3 py-2 text-left">Drawing #</th>
          <th className="min-w-[230px] px-3 py-2 text-left">Drawing title</th>
          <th className="text-left px-3 py-2 hidden md:table-cell">Discipline</th>
          <th className="w-[110px] px-3 py-2 text-left hidden lg:table-cell">Revision</th>
          <th className="min-w-[230px] px-3 py-2 text-left hidden lg:table-cell">Linked work</th>
          <th className="w-[150px] px-3 py-2 text-left hidden xl:table-cell">Updated</th>
          <th className="text-right px-3 py-2 w-[120px]">Actions</th>
        </tr>
      </thead>
      <tbody>
        {pdfs.map((pdf) => {
          const isChecked = selectedSet.has(pdf.id);
          const disciplines = pdf.disciplines && pdf.disciplines.length > 0 ? pdf.disciplines : [UNASSIGNED_LABEL];
          const linkedRfis = pdf.linked_rfi_count ?? 0;
          const linkedSubmittals = pdf.linked_submittal_count ?? 0;
          const linkedIssues = pdf.linked_issue_count ?? 0;
          const sharedMarkups = pdf.shared_markup_count ?? 0;
          const hasLinkedWork = linkedRfis + linkedSubmittals + linkedIssues + sharedMarkups > 0;
          const uploaderName = [pdf.uploaded_by?.first_name, pdf.uploaded_by?.last_name].filter(Boolean).join(' ')
            || pdf.uploaded_by?.email
            || '';
          return (
            <tr
              key={pdf.id}
              data-tour-id="drawings-row"
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('[data-no-row-click]')) return;
                onOpen(pdf);
              }}
              className={cn(
                'border-t cursor-pointer transition-colors',
                isChecked ? 'bg-primary/5' : 'hover:bg-accent/30'
              )}
            >
              <td className="px-3 py-2 align-middle" data-no-row-click>
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={(value) => onToggleSelection(pdf.id, value === true)}
                  aria-label={`Select ${pdf.original_filename}`}
                />
              </td>
              <td className="px-3 py-2 align-middle font-mono text-xs">
                {pdf.primary_page_id && pdf.can_manage_drawings ? (
                  <InlineMetadataField
                    value={pdf.primary_drawing_number || ''}
                    emptyLabel="Add number"
                    ariaLabel={`Edit drawing number for ${pdf.primary_title || pdf.original_filename}`}
                    inputClassName="w-[110px] font-mono font-semibold"
                    valueClassName="font-mono font-semibold text-foreground"
                    onSave={(drawingNumber) => onUpdateMetadata(pdf, pdf.primary_page_id!, {
                      drawing_number: drawingNumber,
                    })}
                  />
                ) : (
                  <span className={cn('px-1.5', pdf.primary_drawing_number ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
                    {pdf.primary_drawing_number || '—'}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 align-middle">
                {pdf.primary_page_id && pdf.can_manage_drawings ? (
                  <InlineMetadataField
                    value={pdf.primary_title || ''}
                    emptyLabel="Add drawing title"
                    ariaLabel={`Edit drawing title for ${pdf.original_filename}`}
                    inputClassName="w-[220px]"
                    valueClassName="font-medium text-foreground"
                    onSave={(title) => onUpdateMetadata(pdf, pdf.primary_page_id!, { title })}
                  />
                ) : (
                  <span className="block max-w-[260px] truncate font-medium">
                    {pdf.primary_title || pdf.original_filename}
                  </span>
                )}
                <span className="mt-0.5 block max-w-[280px] truncate px-1.5 text-[11px] text-muted-foreground" title={pdf.original_filename}>
                  {pdf.original_filename}
                </span>
              </td>
              <td className="px-3 py-2 align-middle hidden md:table-cell">
                {pdf.primary_page_id && pdf.can_manage_drawings && disciplines.length === 1 ? (
                  <InlineMetadataField
                    value={disciplines[0] === UNASSIGNED_LABEL ? '' : disciplines[0]}
                    emptyLabel="Set discipline"
                    ariaLabel={`Edit discipline for ${pdf.primary_title || pdf.original_filename}`}
                    inputClassName="w-[140px]"
                    valueClassName="text-xs text-foreground"
                    onSave={(discipline) => onUpdateMetadata(pdf, pdf.primary_page_id!, { discipline })}
                  />
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {disciplines.map((discipline) => (
                      <span
                        key={`${pdf.id}-row-${discipline}`}
                        className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                      >
                        {discipline}
                      </span>
                    ))}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 align-middle hidden lg:table-cell">
                {pdf.primary_revision_label ? (
                  <span className="inline-flex rounded-md border border-primary/25 bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                    {pdf.primary_revision_label}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">No revision</span>
                )}
              </td>
              <td className="px-3 py-2 align-middle hidden lg:table-cell">
                {hasLinkedWork ? (
                  <div className="flex flex-wrap gap-1">
                    {linkedRfis > 0 && <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold">RFI {linkedRfis}</span>}
                    {linkedSubmittals > 0 && <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold">SUB {linkedSubmittals}</span>}
                    {linkedIssues > 0 && <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold">ISSUE {linkedIssues}</span>}
                    {sharedMarkups > 0 && <span className="rounded-md border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">MARK {sharedMarkups}</span>}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">No linked work</span>
                )}
              </td>
              <td className="px-3 py-2 align-middle hidden xl:table-cell text-xs text-muted-foreground">
                <span className="block text-foreground">{formatRelativeTime(pdf.last_activity_at || pdf.updated_at)}</span>
                {uploaderName && <span className="mt-0.5 block max-w-[140px] truncate text-[11px]">by {uploaderName}</span>}
              </td>
              <td className="px-3 py-2 align-middle text-right">
                <div className="flex justify-end">
                  <RowActions
                    pdf={pdf}
                    onRowAction={onRowAction}
                    onUploadRevision={onUploadRevision}
                    isRowActionRunning={isRowActionRunning}
                  />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
